<template>
  <p :class="$style.backLink"><button type="button" @click="onClickBackLink">&lt; 戻る</button></p>
  <div :class="$style.content">
    <h1 :class="$style.pageTitle">2段階認証</h1>
    <p :class="$style.lead">
      6桁の認証コードをメールアドレスにお送りしました。<br />
      認証コードを入力してログインしてください。
    </p>
    <Form :validation-schema="validationSchema" @submit="onSubmit">
      <div :class="$style.formInner">
        <ITextField v-model.trim="formData.code" name="code" title="認証コード" maxlength="6" inputmode="numeric" autocomplete="one-time-code" placeholder="6桁の認証コードを入力" hankaku />
        <div v-if="skippable" :class="$style.skip">
          <label><input v-model="formData.isTrustedDevice" name="isTrustedDevice" type="checkbox" /> 以降、このブラウザでは認証をスキップする</label>
        </div>
      </div>
      <IButton type="primary" native-type="submit" :class="$style.button">認証する</IButton>
      <p :class="$style.retry"><button type="button" @click="onClickResendButton">認証コードを再送する</button></p>
      <p>
        <a href="https://help.iichi.com/hc/ja/articles/360000013721" target="_blank">ログインでお困りの方</a>
      </p>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { v4 as uuidv4 } from 'uuid';
import { Form } from 'vee-validate';
import { reactive, onMounted } from 'vue';
import * as z from 'zod';
import { resendCode, verifyCode } from '@/js/lib/api/v1/signin/mfa-auth';
import { info, loading } from '@/js/lib/ui';
import IButton from '@/js/components/molecules/IButton.vue';
import ITextField from '@/js/components/molecules/ITextField.vue';

interface FormData {
  code: string;
  isTrustedDevice: boolean;
}

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'success'): void;
}>();
const formData = reactive<FormData>({
  code: '',
  isTrustedDevice: false,
});
const validationSchema = toTypedSchema(
  z.object({
    code: z.string().regex(/^\d{6}$/, {
      message: '6桁の数字を入力してください',
    }),
  }),
);
const skippable = (() => {
  try {
    // プライベートブラウズ時などでは localStorage を参照するだけでエラーになる
    return typeof localStorage !== 'undefined' && localStorage !== null;
  } catch {
    return false;
  }
})();
const onSubmit = () => {
  loading({
    asyncFunc: async () => {
      try {
        const trustedDeviceId = formData.isTrustedDevice ? uuidv4() : undefined;
        await verifyCode({ code: formData.code, trustedDeviceId });
        if (trustedDeviceId) {
          localStorage.setItem('trusted-device-id', trustedDeviceId);
        }
        await info('認証に成功しました', { fadeOut: true });
        emit('success');
      } catch (error) {
        window.Sentry?.captureException(error);
        await info(error instanceof Error ? error.message : '認証に失敗しました');
      }
    },
  });
};
const onClickBackLink = () => {
  emit('close');
};

const onClickResendButton = () => {
  loading({
    asyncFunc: async () => {
      try {
        await resendCode();
        await info('認証コードを再送しました', { fadeOut: true });
        formData.code = '';
      } catch (error) {
        window.Sentry?.captureException(error);
        await info(error instanceof Error ? error.message : '認証コードの再送に失敗しました');
      }
    },
  });
};

onMounted(() => {
  const modalEl = document.querySelector('.el-dialog.is-fullscreen');
  if (modalEl) {
    modalEl.scrollTop = 0;
  }
});
</script>

<style module lang="scss">
.backLink {
  left: 20px;
  position: absolute;
  top: 25px;

  > button {
    font-size: 16px;

    @media (--sp) {
      font-size: 14px;
    }
  }
}

.content {
  margin: 0 auto;
  max-width: 480px;
  text-align: center;
  width: 100%;
}

.pageTitle {
  font-size: 24px;
  margin-bottom: 30px;
}

.lead {
  margin-bottom: 30px;
  text-align: left;
}

.formInner {
  margin-bottom: 30px;

  .skip {
    font-size: 14px;
    padding-top: 8px;
    text-align: left;

    label {
      cursor: pointer;
    }
  }
}

.button {
  margin-bottom: 10px;
}

.retry {
  font-size: 14px;
  margin-bottom: 45px;

  button {
    border-bottom: 1px dashed var(--color-gray900);
  }
}
</style>
